import { useState, useEffect } from "react";
import { getResumes, createResume, updateResume, deleteResume } from "../services/resumeService";
import { getCoverLetters, createCoverLetter, updateCoverLetter, deleteCoverLetter } from "../services/coverLetterService";
import { getApplications } from "../services/applicationService";
import ResumeModal from "../components/resumeModal";
import CoverLetterModal from "../components/coverLetterModal";
import ConfirmModal from "../components/confirmModal";
import { toast } from "../utils/toast";

const STATUS_COLORS = {
  Saved:     "#6b7280",
  Applied:   "#2563eb",
  OA:        "#0891b2",
  Interview: "#ff9d00",
  Accepted:  "#16a34a",
  Rejected:  "#ef4444",
};

export default function DocumentsPage() {
  const [applications, setApplications] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [coverLetters, setCoverLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  const [resumeModal, setResumeModal] = useState(null);
  const [clModal, setClModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const [resumeData, clData, appData] = await Promise.all([
          getResumes(),
          getCoverLetters(),
          getApplications(),
        ]);
        setResumes(resumeData || []);
        setCoverLetters(clData || []);
        setApplications(appData || []);
      } catch (err) {
        console.error("Failed to load documents", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleResumeSubmit = async ({ title, file }) => {
    try {
      if (resumeModal?.id) {
        const updated = await updateResume(resumeModal.id, { title, file: file || undefined });
        setResumes(resumes.map((r) => (r.id === updated.id ? updated : r)));
      } else {
        const created = await createResume({ title, file });
        setResumes([...resumes, created]);
      }
    } catch (err) {
      console.error(err);
      toast("Failed to save resume");
    }
  };

  const handleDeleteResume = async (id) => {
    try {
      await deleteResume(id);
      setResumes(resumes.filter((r) => r.id !== id));
    } catch (err) {
      console.error(err);
      toast("Failed to delete resume");
    }
  };

  const handleClSubmit = async ({ title, language, application_id, file, content }) => {
    try {
      if (clModal?.id) {
        const updated = await updateCoverLetter(clModal.id, { title, language, application_id, file: file || undefined, content });
        setCoverLetters(coverLetters.map((cl) => (cl.id === updated.id ? updated : cl)));
      } else {
        const created = await createCoverLetter({ title, language, application_id, file, content });
        setCoverLetters([...coverLetters, created]);
      }
    } catch (err) {
      console.error(err);
      toast("Failed to save cover letter");
    }
  };

  const handleDeleteCl = async (id) => {
    try {
      await deleteCoverLetter(id);
      setCoverLetters(coverLetters.filter((cl) => cl.id !== id));
    } catch (err) {
      console.error(err);
      toast("Failed to delete cover letter");
    }
  };

  return (
    <div className="app-layout">
      <div className="documents-grid">

        {/* ---- RESUMES ---- */}
        <div className="panel-card doc-list-card">
          <div className="doc-col-header">
            <h2>Resumes <span className="doc-count">{resumes.length} total</span></h2>
            <button className="btn-primary-sm" onClick={() => setResumeModal("create")}>+ Add</button>
          </div>

          <div className="cl-scrollable-list">
            {loading ? (
              <p className="doc-empty">Loading...</p>
            ) : resumes.length === 0 ? (
              <div className="doc-empty-state">
                <span className="doc-empty-icon">📄</span>
                <p>No resumes yet</p>
                <span className="doc-empty-hint">Upload a PDF resume to link it to your applications and use AI Review.</span>
                <button className="btn-primary-sm" onClick={() => setResumeModal("create")}>Upload Resume</button>
              </div>
            ) : (
              <ul className="list-scroll">
                {resumes.map((r) => {
                  const usedIn = applications.filter((a) => a.resume_id === r.id);
                  return (
                    <li key={r.id} className="doc-card">
                      <div className="doc-card-left">
                        <span className="doc-card-icon">📄</span>
                        <div className="doc-card-info">
                          <span className="doc-card-title">{r.title}</span>
                          <span className="doc-card-meta">
                            {usedIn.length === 0
                              ? "Not linked to any application"
                              : `Used in ${usedIn.length} application${usedIn.length > 1 ? "s" : ""}`}
                          </span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <button className="btn-icon-edit" title="Edit" onClick={() => setResumeModal(r)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon-delete" title="Delete" onClick={() => setConfirmDelete({ type: "resume", id: r.id })}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* ---- COVER LETTERS ---- */}
        <div className="panel-card doc-list-card">
          <div className="doc-col-header">
            <h2>Cover Letters <span className="doc-count">{coverLetters.length} total</span></h2>
            <button className="btn-primary-sm" onClick={() => setClModal("create")}>+ Add</button>
          </div>

          <div className="cl-scrollable-list">
            {loading ? (
              <p className="doc-empty">Loading...</p>
            ) : coverLetters.length === 0 ? (
              <div className="doc-empty-state">
                <span className="doc-empty-icon">✉️</span>
                <p>No cover letters yet</p>
                <span className="doc-empty-hint">Upload or paste a cover letter to link it to an application and get AI feedback.</span>
                <button className="btn-primary-sm" onClick={() => setClModal("create")}>Add Cover Letter</button>
              </div>
            ) : (
              <ul className="list-scroll">
                {coverLetters.map((cl) => {
                  const linkedApp = applications.find((a) => a.id === cl.application_id);
                  const statusColor = STATUS_COLORS[linkedApp?.status] || "#9ca3af";
                  return (
                    <li key={cl.id} className="doc-card">
                      <div className="doc-card-left">
                        <span className="doc-card-icon">✉️</span>
                        <div className="doc-card-info">
                          <span className="doc-card-title">{cl.title}</span>
                          <span className="doc-card-meta">
                            {linkedApp ? (
                              <>
                                {linkedApp.job?.company || linkedApp.title}
                                {cl.language ? ` · ${cl.language}` : ""}
                                <span className="doc-card-status" style={{ backgroundColor: statusColor }}>
                                  {linkedApp.status}
                                </span>
                              </>
                            ) : (
                              <>No linked application{cl.language ? ` · ${cl.language}` : ""}</>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <button className="btn-icon-edit" title="Edit" onClick={() => setClModal(cl)}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon-delete" title="Delete" onClick={() => setConfirmDelete({ type: "cl", id: cl.id })}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

      </div>

      {confirmDelete && (
        <ConfirmModal
          message={`Are you sure you want to delete this ${confirmDelete.type === "resume" ? "resume" : "cover letter"}?`}
          onConfirm={() => {
            if (confirmDelete.type === "resume") handleDeleteResume(confirmDelete.id);
            else handleDeleteCl(confirmDelete.id);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {resumeModal && (
        <ResumeModal
          onClose={() => setResumeModal(null)}
          onSubmit={handleResumeSubmit}
          initialResume={resumeModal === "create" ? null : resumeModal}
        />
      )}

      {clModal && (
        <CoverLetterModal
          onClose={() => setClModal(null)}
          onSubmit={handleClSubmit}
          initialCl={clModal === "create" ? null : clModal}
          applications={applications}
        />
      )}
    </div>
  );
}
